import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import ContactDetailsStage from "../../components/signup/contactDetailsStage";
import FighterFormBasicStage from "../../components/signup/fighterFormBasicStage";
import FighterFormDetailsStage from "../../components/signup/fighterFormDetailsStage";
import FightHistoryStage from "../../components/signup/fightHistoryStage";
import LocationDetailsStage from "../../components/signup/locationDetailsStage";
import ManagerFormStage from "../../components/signup/managerFormStage";
import PersonalDetailsStage from "../../components/signup/personalDetailsStage";
import ProfileImageUploadStage from "../../components/signup/profileImageUploadStage";
import RoleSelectionStage from "../../components/signup/roleSelectionStage";
import VideoUploadStage from "../../components/signup/videoUploadStage";

import SignUpSetupLogics from "../../features/signUpSetup/signUpSetup";
import {
  fetchTrainingDisciplines_signup,
  fetchWeightClasses_signup,
} from "../../services/api";
import { fighterSponsorsRequestDtos } from "../../types/types";
import { formatText } from "../../utils/common/formatUtils";
import { isValidUrl } from "../../utils/utilMethods";
import SponsorStage from "../../components/signup/sponsorStage";
import LoadingScreen from "../../components/common/loadingScreen";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

const socialMediaPlatforms = [
  "YouTube",
  "Facebook",
  "Instagram",
  "Twitter",
  "LinkedIn",
];

const SignUpSetupPage: React.FC = () => {
  const { t, i18n } = useTranslation("signUpSetupStages");

  const [sports, setSports] = useState<string[]>([]);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const {
    state,
    countries,
    regions,
    isSubmitting,
    selectedTrainingDisciplines,
    selectedWeightClasses,
    apiError,
    validationErrors,
    updateSponsorLink,
    handleRoleSelection,
    handleNext,
    handleFormChange,
    toggleMultiSelectOption,
    handleFileChange,
    removeVideo,
    addYouTubeLink,
    updateYouTubeLink,
    removeYouTubeLink,
    handleFormSubmit,
    handleBack,
    handleErrorClose,
    handleFightHistoriesChange,
    handleSponsorLogoUpload,
    addSocialMediaLink,
    updateSocialMediaLink,
    removeSocialMediaLink,
    showSuccessPopup,
    activeMember,
    setActiveMember,
    handleSuccessPopupClose,
  } = SignUpSetupLogics();

  const removeSponsorLink = (index: number) => {
    const updatedSponsors = [
      ...(state.formData.fighterDto?.fighterSponsorsRequestDtos || []),
    ];
    updatedSponsors.splice(index, 1);
    handleFormChange(
      updatedSponsors,
      "fighterSponsorsRequestDtos",
      "fighterDto"
    );
  };

  const addSponsorLink = () => {
    if (
      (state.formData.fighterDto?.fighterSponsorsRequestDtos?.length ?? 0) >= 5
    )
      return;

    const newSponsor: fighterSponsorsRequestDtos = {
      name: "",
      logo: "",
      link: "",
      description: "",
    };

    const updatedSponsors = [
      ...(state.formData.fighterDto?.fighterSponsorsRequestDtos || []),
      newSponsor,
    ];

    handleFormChange(
      updatedSponsors,
      "fighterSponsorsRequestDtos",
      "fighterDto"
    );
  };

  const loadSportsIfNeeded = async () => {
    if (sports.length === 0) {
      try {
        const fetched = await fetchTrainingDisciplines_signup();
        setSports(fetched);
      } catch (error) {
        console.error("Failed to load disciplines", error);
      }
    }
  };

  const loadweightclassIfNeeded = async () => {
    if (weightClasses.length === 0) {
      try {
        const weight = await fetchWeightClasses_signup();
        setWeightClasses(weight);
      } catch (error) {
        console.error("Failed to load disciplines", error);
      }
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  if (state.stage === "navigate") {
    return <Navigate to="/dashboard" replace />;
  }

  if (showSuccessPopup) {
    const displayEmail =
      state.formData.email ||
      (state.formData as any).fighterDto?.email ||
      (state.formData as any).managerDto?.email ||
      "";

    return (
      <div className="flex min-h-dvh w-full bg-white font-[\'Plus_Jakarta_Sans\',sans-serif] text-[#18181B]">
        {/* LEFT COLUMN */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            className="flex min-h-dvh flex-col justify-between px-6 py-6 sm:px-12 sm:py-8 lg:py-9 [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6 [@media(max-height:700px)]:lg:py-3"
          >
            {/* Header: Logo */}
            <div className="flex w-full items-center justify-start">
              <img
                src={hoowlrLogo}
                alt="Hoowlr"
                className="h-9 w-auto object-contain object-left lg:h-16 lg:w-48 [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14 [@media(max-height:700px)]:lg:h-10"
              />
            </div>

            {/* Center Content */}
            <div className="my-auto flex flex-col items-center justify-center text-center px-4 max-w-lg mx-auto">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#18181B] mb-6 tracking-tight">
                {t("successModal.title", { defaultValue: "Registration successful" })}
              </h1>

              <div className="w-12 h-12 rounded-full bg-[#E7F8EE] flex items-center justify-center mb-6">
                <svg
                  className="w-6 h-6 text-[#16A34A]"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
              </div>

              <p className="text-sm text-[#71717A] leading-relaxed mb-8 max-w-md">
                Your registration is complete. We&apos;ve sent a confirmation email to{" "}
                {displayEmail ? (
                  <span className="font-semibold text-[#18181B]">{displayEmail}</span>
                ) : (
                  <span>[email address]</span>
                )}
                .<br />
                Follow the instructions in the email to confirm your account.
              </p>

              <Button
                onPress={handleSuccessPopupClose}
                className="h-10 px-8 rounded-full bg-[#0485F7] text-sm font-semibold text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm"
              >
                {t("successModal.returnHome", { defaultValue: "Return to home page" })}
              </Button>
            </div>

            {/* Footer spacer */}
            <div className="h-9 lg:h-16" />
          </div>
        </div>

        {/* RIGHT COLUMN - BANNER */}
        <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
          <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
            <img
              src={loginBanner}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          </div>
        </div>
      </div>
    );
  }

  const selectedUnit = state.formData.fighterDto?.heightUnit || "cm";

  const heightFieldProps = {
    cm: { min: 100, max: 250, placeholder: "e.g., 100 - 250" },
    ft: { min: 3, max: 8, placeholder: "e.g., 3 - 8" },
    inch: { min: 36, max: 96, placeholder: "e.g., 36 -96" },
  }[selectedUnit] || { min: 100, max: 250, placeholder: "e.g., 100 - 250" };

  const selectedWeightUnit = state.formData.fighterDto?.weightUnit || "kgs";

  const weightFieldProps = {
    kgs: { min: 30, max: 200, placeholder: "e.g., 30 - 200" },
    lbs: { min: 66, max: 440, placeholder: "e.g., 66 - 440" },
  }[selectedWeightUnit] || { min: 30, max: 200, placeholder: "e.g., 30 - 200" };

  const selectedReachUnit = state.formData.fighterDto?.reachUnit || "cm";

  const reachFieldProps = {
    cm: { min: 100, max: 250, placeholder: "e.g., 100 - 250" },
    ft: { min: 3, max: 8, placeholder: "e.g., 3 - 8" },
    inch: { min: 36, max: 100, placeholder: "e.g., 36 - 100" },
  }[selectedReachUnit] || {
    min: 100,
    max: 250,
    placeholder: "e.g., 100 - 250",
  };

  return (
    <div className="min-h-screen w-full bg-white flex flex-col">
      <Modal
        isOpen={apiError !== null}
        onClose={handleErrorClose}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader className="flex gap-2 items-center">
            <div className="flex-shrink-0 w-8 h-8 bg-red-50 rounded-full flex items-center justify-center">
              <svg
                className="w-4 h-4 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M5.071 19h13.858c1.54 0 2.502-1.667 1.732-3L13.732 3c-.77-1.333-2.694-1.333-3.464 0L3.339 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800">
              {t("errorModal.title")}
            </h3>
          </ModalHeader>
          <ModalBody>
            <p className="text-gray-600 text-sm leading-relaxed">{apiError}</p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="solid"
              onPress={handleErrorClose}
              size="sm"
              className="w-full text-sm"
            >
              {t("errorModal.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {state.stage === "loading" && <LoadingScreen />}

      {/* Role selection */}
      {state.stage === "roleSelection" && (
        <RoleSelectionStage t={t} handleRoleSelection={handleRoleSelection} />
      )}

      {/* Personal details */}
      {state.stage === "personalDetails" && (
        <PersonalDetailsStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          handleFormChange={handleFormChange}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      )}

      {/* Location details */}
      {state.stage === "locationDetails" && (
        <LocationDetailsStage
          t={t}
          state={state}
          countries={countries}
          regions={regions}
          validationErrors={validationErrors}
          handleFormChange={handleFormChange}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      )}

      {/* Contact details */}
      {state.stage === "contactDetails" && (
        <ContactDetailsStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          handleFormChange={handleFormChange}
          updateSocialMediaLink={updateSocialMediaLink}
          addSocialMediaLink={addSocialMediaLink}
          removeSocialMediaLink={removeSocialMediaLink}
          socialMediaPlatforms={socialMediaPlatforms}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      )}

      {/* Profile image upload */}
      {state.stage === "profileImageUpload" && (
        <ProfileImageUploadStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          handleFileChange={handleFileChange}
          handleNext={handleNext}
          handleFormSubmit={handleFormSubmit}
          handleBack={handleBack}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Fighter form basic */}
      {state.stage === "fighterFormBasic" && (
        <FighterFormBasicStage
          t={t}
          state={state}
          sports={sports}
          weightClasses={weightClasses}
          selectedTrainingDisciplines={selectedTrainingDisciplines}
          selectedWeightClasses={selectedWeightClasses}
          validationErrors={validationErrors}
          heightFieldProps={heightFieldProps}
          weightFieldProps={weightFieldProps}
          reachFieldProps={reachFieldProps}
          loadSportsIfNeeded={loadSportsIfNeeded}
          loadweightclassIfNeeded={loadweightclassIfNeeded}
          toggleMultiSelectOption={toggleMultiSelectOption}
          handleFormChange={handleFormChange}
          handleNext={handleNext}
          handleBack={handleBack}
          formatText={formatText}
        />
      )}

      {/* Fighter form details */}
      {state.stage === "fighterFormDetails" && (
        <FighterFormDetailsStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          handleFormChange={handleFormChange}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      )}

      {/* Fight history */}
      {state.stage === "fightHistory" && (
        <FightHistoryStage
          t={t}
          state={state}
          countries={countries}
          handleFightHistoriesChange={handleFightHistoriesChange}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      )}

      {/* Manager form */}
      {state.stage === "managerForm" && (
        <ManagerFormStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          handleFormChange={handleFormChange}
          handleFormSubmit={handleFormSubmit}
          handleBack={handleBack}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Video upload */}
      {state.stage === "videoUpload" && (
        <VideoUploadStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          handleFileChange={handleFileChange}
          removeVideo={removeVideo}
          addYouTubeLink={addYouTubeLink}
          updateYouTubeLink={updateYouTubeLink}
          removeYouTubeLink={removeYouTubeLink}
          isValidURL={isValidUrl}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      )}

      {/* Sponsor stage */}
      {state.stage === "sponsorStage" && (
        <SponsorStage
          t={t}
          state={state}
          validationErrors={validationErrors}
          addSponsorLink={addSponsorLink}
          updateSponsorLink={updateSponsorLink}
          removeSponsorLink={removeSponsorLink}
          handleSponsorLogoUpload={handleSponsorLogoUpload}
          isValidURL={isValidUrl}
          handleFormSubmit={handleFormSubmit}
          handleBack={handleBack}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default SignUpSetupPage;